import { Link } from "react-router-dom";
import { AlarmClock, ArrowRight, CalendarDays, Check, ChevronRight, Circle, Crown, Flame, FolderOpen, ScanLine, Users } from "lucide-react";
import { Avatar } from "@/components/ContactRow";
import { fmtDate, greeting } from "@/lib/toto";
import { PremiumBadge } from "@/components/PremiumBadge";

const Kpi = ({ icon: Icon, label, value, fg, bg, to, testid }) => (
  <Link to={to} className="t-card p-5 flex items-center gap-4 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-[0_10px_28px_-16px_rgba(15,23,42,.35)]" data-testid={testid}>
    <span className="w-12 h-12 rounded-[14px] flex items-center justify-center" style={{ background: bg }}><Icon className="w-6 h-6" style={{ color: fg }} /></span>
    <span><span className="block text-[28px] font-extrabold leading-none">{value}</span><span className="block mt-1 text-[13px] font-semibold text-[#64748B]">{label}</span></span>
  </Link>
);

const Panel = ({ title, to, link = "View All", children, className = "", testid }) => (
  <section className={`t-card p-5 ${className}`} data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-[17px] font-bold">{title}</h2>
      {to && <Link to={to} className="t-link inline-flex items-center gap-1">{link}<ArrowRight className="w-4 h-4" /></Link>}
    </div>
    {children}
  </section>
);

export default function HomeDesktop({ user, usage, promoImg, promo, counts, contacts, events, goals, due }) {
  const daily = goals?.goals?.daily ?? [];
  const done = daily.filter((g) => g.completed).length;
  const streak = goals?.streak?.currentStreak ?? 0;
  const cats = (counts?.categories ?? []).filter((c) => c.count > 0).sort((a, b) => b.count - a.count);
  const maxCat = Math.max(1, ...cats.map((c) => c.count));
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="hidden lg:block px-10 py-8 max-w-[1400px] mx-auto fade-up" data-testid="home-desktop">
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 relative overflow-hidden rounded-[24px] p-7 text-white bg-gradient-to-br from-[#0000EC] to-[#4338CA] flex items-center gap-8">
          <div className="absolute -left-16 -bottom-24 w-72 h-72 rounded-full bg-white/5" />
          <div className="absolute left-1/3 -top-24 w-64 h-64 rounded-full bg-white/10" />
          <div className="relative flex-1 min-w-0">
            <p className="text-[14px] text-white/75 flex items-center gap-2">{today}{usage?.isPremium && <PremiumBadge size="sm" testid="home-desktop-premium-badge" />}</p>
            <h1 className="mt-1 text-4xl font-extrabold" data-testid="home-desktop-greeting">{greeting()}, {user?.name?.split(" ")[0] ?? "there"}</h1>
            <p className="mt-2 text-[16px] text-white/85 max-w-md">Turn every card into a connection. Scan, organise and follow up — all in one place.</p>
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <Link to="/scan" className="t-btn bg-white text-[#0000EC] h-12 hover:bg-white/90" data-testid="home-desktop-scan"><ScanLine className="w-5 h-5" />Scan a Card</Link>
              <Link to="/contacts" className="t-btn h-12 bg-white/15 text-white hover:bg-white/25">View Contacts</Link>
            </div>
            <span className="inline-flex items-center gap-1.5 mt-5 text-[13px] font-semibold bg-white/15 rounded-full px-3 py-1.5" data-testid="home-desktop-usage">
              {usage?.isPremium ? <><Crown className="w-4 h-4" />Premium · Unlimited scans</> : usage ? `${usage.remainingScans} free scans left` : "…"}
              {streak > 0 && <><span className="mx-1 opacity-50">|</span><Flame className="w-4 h-4" />{streak} day streak</>}
            </span>
          </div>
          {promoImg && (
            <a href={promo.linkUrl || undefined} target="_blank" rel="noreferrer" className="relative w-[46%] max-w-[600px] shrink-0 aspect-[2.6/1] rounded-[18px] overflow-hidden ring-4 ring-white/15 shadow-[0_20px_40px_-20px_rgba(0,0,0,.5)] transition-transform hover:-translate-y-0.5" data-testid="home-desktop-banner">
              <img src={promoImg} alt={promo.title || "Promotion"} className="w-full h-full object-cover" />
            </a>
          )}
        </div>

        <div className="col-span-12 grid grid-cols-4 gap-6">
          <Kpi icon={Users} label="Contacts" value={counts?.total ?? contacts.length} fg="#2563EB" bg="#EFF6FF" to="/contacts" testid="home-kpi-contacts" />
          <Kpi icon={CalendarDays} label="Events" value={events.length} fg="#EA580C" bg="#FFF7ED" to="/events" testid="home-kpi-events" />
          <Kpi icon={FolderOpen} label="Categories" value={cats.length} fg="#7C3AED" bg="#F5F3FF" to="/contacts" testid="home-kpi-categories" />
          <Kpi icon={AlarmClock} label="Follow-ups due" value={due.length} fg="#DC2626" bg="#FEF2F2" to={due[0] ? `/contacts/${due[0].contactId}` : "/contacts"} testid="home-kpi-followups" />
        </div>

        <Panel title="Recent Contacts" to="/contacts" className="col-span-8" testid="home-desktop-contacts">
          <div className="divide-y divide-[#F1F5F9] -mx-2">
            {contacts.slice(0, 6).map((c) => (
              <Link key={c.id} to={`/contacts/${c.id}`} className="flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-[#F8FAFC] transition-colors">
                <Avatar name={c.name || c.companyName} size={38} />
                <span className="flex-1 min-w-0 grid grid-cols-[1.2fr_1fr_1fr] gap-4 items-center">
                  <span className="min-w-0"><span className="block text-[15px] font-bold truncate">{c.name || c.companyName}</span><span className="block text-[13px] text-[#64748B] truncate">{c.designation}</span></span>
                  <span className="text-[14px] text-[#334155] truncate">{c.companyName || "—"}</span>
                  <span className="text-[14px] text-[#64748B] truncate">{c.phone || c.email || "—"}</span>
                </span>
                <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
              </Link>
            ))}
            {!contacts.length && <p className="px-2 py-6 text-[14px] text-[#64748B]">No contacts yet. Scan your first visiting card.</p>}
          </div>
        </Panel>

        <Panel title="Today's Goals" to="/goals" link="View Goals" className="col-span-4" testid="home-desktop-goals">
          <div className="flex items-center justify-between">
            <span className="text-[15px] font-bold text-[#7C3AED]">{done} / {daily.length} completed</span>
            {streak > 0 && <span className="flex items-center gap-1 rounded-full bg-[#FEF9C3] px-2.5 py-1 text-[12px] font-bold text-[#B45309]"><Flame className="w-3.5 h-3.5 fill-[#EA580C] text-[#EA580C]" />{streak} day streak</span>}
          </div>
          <div className="mt-2.5 h-2 rounded-full bg-[#F5F3FF] overflow-hidden"><div className="h-full bg-[#7C3AED] rounded-full" style={{ width: `${daily.length ? (done / daily.length) * 100 : 0}%` }} /></div>
          <ul className="mt-4 space-y-2.5">
            {daily.map((g) => (
              <li key={g.key} className={`flex items-center gap-2.5 text-[14px] ${g.completed ? "font-semibold text-[#059669]" : "text-[#334155]"}`}>
                {g.completed ? <Check className="w-4 h-4" strokeWidth={3} /> : <Circle className="w-3.5 h-3.5 text-[#CBD5E1]" />}
                <span className="flex-1">{g.title}</span><span className="text-[12px] text-[#94A3B8]">{Math.min(g.progress, g.target)}/{g.target}</span>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="Recent Events" to="/events" className="col-span-8" testid="home-desktop-events">
          {events.length ? (
            <div className="grid grid-cols-2 gap-3">
              {events.slice(0, 4).map((ev) => (
                <Link key={ev.id} to={`/events/${ev.id}`} className="flex items-center gap-3 p-3 rounded-[14px] border border-[#E2E8F0] hover:border-[#0000EC]/40 transition-colors">
                  <span className="w-11 h-11 rounded-[12px] bg-[#E5E5FD] flex flex-col items-center justify-center text-[#0000EC] leading-none">
                    <span className="text-[10px] font-bold uppercase">{fmtDate(ev.eventDate).split(" ")[1]}</span><span className="text-[16px] font-extrabold">{fmtDate(ev.eventDate).split(" ")[0]}</span>
                  </span>
                  <span className="min-w-0"><span className="block text-[15px] font-bold truncate">{ev.name}</span><span className="text-[13px] text-[#64748B]">{ev.contactsCount} contact{ev.contactsCount === 1 ? "" : "s"}{ev.location ? ` · ${ev.location}` : ""}</span></span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-4 rounded-[14px] bg-[#F8FAFC] p-4">
              <CalendarDays className="w-8 h-8 text-[#0000EC]" />
              <p className="flex-1 text-[14px] text-[#475569]">No events yet. Create one to group the people you meet.</p>
              <Link to="/events" className="t-btn t-btn-outline h-10 text-[14px] px-4">Create Event</Link>
            </div>
          )}
        </Panel>

        <Panel title="Categories" to="/contacts" className="col-span-4" testid="home-desktop-categories">
          <div className="space-y-3">
            {cats.slice(0, 6).map((c) => (
              <Link key={c.id} to={`/contacts?categoryId=${c.id}`} className="block group">
                <div className="flex justify-between text-[14px] mb-1"><span className="font-semibold group-hover:text-[#0000EC]">{c.name}</span><span className="text-[#64748B]">{c.count}</span></div>
                <div className="h-2 rounded-full bg-[#F1F5F9]"><div className="h-full rounded-full bg-[#0000EC]" style={{ width: `${(c.count / maxCat) * 100}%` }} /></div>
              </Link>
            ))}
            {!cats.length && <p className="text-[14px] text-[#64748B]">Categories appear as you save contacts.</p>}
          </div>
        </Panel>
      </div>
    </div>
  );
}
